'use client';

import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit, Plus, ChevronDown, ChevronRight, CornerDownRight } from 'lucide-react';

interface Category {
  id: number;
  code: string;
  parent_id: number | null;
  translations: Record<string, string[]>;
  created_at: string;
  updated_at: string;
}

interface Language {
  code: string;
  description: string;
}

const CategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubcategory, setIsSubcategory] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const [formData, setFormData] = useState({
    translations: {} as Record<string, string[]>,
  });
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['HU']);

  // Fetch categories és languages
  useEffect(() => {
    fetchCategories();
    fetchLanguages();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/categories');
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Hiba a kategóriák lekérésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await fetch('/api/admin/languages');
      const data = await response.json();
      setLanguages(data.languages || []);
    } catch (error) {
      console.error('Hiba a nyelvek lekérésekor:', error);
    }
  };

  const getSubcategories = (categoryId: number) => {
    return categories.filter(cat => cat.parent_id === categoryId);
  };

  const toggleCategoryExpanded = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const openDialog = (category?: Category, asSubcategory?: boolean, parentId?: number) => {
    if (category) {
      setIsEditing(true);
      setEditingId(category.id);
      setIsSubcategory(false);
      setParentCategoryId(null);
      setFormData({
        translations: { ...category.translations },
      });
      setSelectedLanguages(['HU', ...Object.keys(category.translations).filter(lang => lang !== 'HU')]);
    } else {
      setIsEditing(false);
      setEditingId(null);
      setIsSubcategory(asSubcategory || false);
      setParentCategoryId(parentId || null);
      setFormData({
        translations: { HU: [''] },
      });
      setSelectedLanguages(['HU']);
    }
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setFormData({
      translations: {},
    });
    setIsSubcategory(false);
    setParentCategoryId(null);
  };

  const handleSave = async () => {
    if (!formData.translations.HU || formData.translations.HU[0].trim() === '') {
      alert('A magyar név kötelező!');
      return;
    }

    try {
      let url: string;
      let method: string;

      if (isSubcategory && parentCategoryId) {
        // Alkategória kezelése
        if (isEditing) {
          url = `/api/admin/categories/${parentCategoryId}/subcategories/${editingId}`;
          method = 'PATCH';
        } else {
          url = `/api/admin/categories/${parentCategoryId}/subcategories`;
          method = 'POST';
        }
      } else {
        // Főkategória kezelése
        if (isEditing) {
          url = `/api/admin/categories/${editingId}`;
          method = 'PATCH';
        } else {
          url = '/api/admin/categories';
          method = 'POST';
        }
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: {
            translations: formData.translations,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Hiba a mentés során');
        return;
      }

      fetchCategories();
      closeDialog();

      // Ha alkategória volt hozzáadva, automatikusan kibontjuk a szülő kategóriát
      if (isSubcategory && parentCategoryId && !isEditing) {
        const newExpanded = new Set(expandedCategories);
        newExpanded.add(parentCategoryId);
        setExpandedCategories(newExpanded);
      }
    } catch (error) {
      console.error('Hiba a mentés során:', error);
      alert('Hiba a mentés során');
    }
  };

  const handleDelete = async (id: number, parentId?: number) => {
    if (!confirm('Biztosan törölni szeretnéd ezt a kategóriát?')) return;

    try {
      let url: string;
      if (parentId) {
        url = `/api/admin/categories/${parentId}/subcategories/${id}`;
        const response = await fetch(url, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const error = await response.json();
          alert(error.error || 'Hiba a törlés során');
          return;
        }
      } else {
        url = `/api/admin/categories/${id}`;
        const response = await fetch(url, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const error = await response.json();
          alert(error.error || 'Hiba a törlés során');
          return;
        }
      }

      fetchCategories();
    } catch (error) {
      console.error('Hiba a törlés során:', error);
      alert('Hiba a törlés során');
    }
  };

  const handleTranslationChange = (languageCode: string, index: number, value: string) => {
    const currentNames = formData.translations[languageCode] || [];
    const newNames = [...currentNames];
    newNames[index] = value;

    setFormData({
      ...formData,
      translations: {
        ...formData.translations,
        [languageCode]: newNames,
      },
    });
  };

  const handleAddAlias = (languageCode: string) => {
    const currentNames = formData.translations[languageCode] || [];
    setFormData({
      ...formData,
      translations: {
        ...formData.translations,
        [languageCode]: [...currentNames, ''],
      },
    });
  };

  const handleRemoveAlias = (languageCode: string, index: number) => {
    const currentNames = formData.translations[languageCode] || [];
    const newNames = currentNames.filter((_, i) => i !== index);

    if (newNames.length === 0 && languageCode === 'HU') {
      return;
    }

    if (newNames.length === 0) {
      const newLanguages = selectedLanguages.filter(lang => lang !== languageCode);
      setSelectedLanguages(newLanguages);
      const newTranslations = { ...formData.translations };
      delete newTranslations[languageCode];
      setFormData({
        ...formData,
        translations: newTranslations,
      });
    } else {
      setFormData({
        ...formData,
        translations: {
          ...formData.translations,
          [languageCode]: newNames,
        },
      });
    }
  };

  const handleAddLanguage = (languageCode: string) => {
    if (!selectedLanguages.includes(languageCode)) {
      setSelectedLanguages([...selectedLanguages, languageCode]);
      setFormData({
        ...formData,
        translations: {
          ...formData.translations,
          [languageCode]: [''],
        },
      });
    }
  };

  const handleRemoveLanguage = (languageCode: string) => {
    if (languageCode === 'HU') return;

    const newLanguages = selectedLanguages.filter(lang => lang !== languageCode);
    setSelectedLanguages(newLanguages);

    const newTranslations = { ...formData.translations };
    delete newTranslations[languageCode];
    setFormData({
      ...formData,
      translations: newTranslations,
    });
  };

  const getAvailableLanguagesToAdd = () => {
    return languages.filter(lang => !selectedLanguages.includes(lang.code));
  };

  const renderCategoryRow = (category: Category, level: number = 0) => {
    const subcategories = getSubcategories(category.id);
    const isExpanded = expandedCategories.has(category.id);
    const hungarianNames = category.translations.HU || [];
    const mainName = hungarianNames[0] || category.code;

    return (
      <React.Fragment key={category.id}>
        <TableRow>
          <TableCell
            style={{ paddingLeft: `${level * 2}rem`, cursor: subcategories.length > 0 ? 'pointer' : 'default' }}
            onClick={() => {
              if (subcategories.length > 0) {
                toggleCategoryExpanded(category.id);
              }
            }}
          >
            <div className="flex items-center gap-2">
              {subcategories.length > 0 && (
                <span className="inline-block h-6 w-6 flex items-center justify-center">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
              )}
              {subcategories.length === 0 && <div className="w-6"></div>}
              {level > 0 && <CornerDownRight className="h-4 w-4 text-muted-foreground" />}
              <span className="font-medium">{mainName}</span>
            </div>
          </TableCell>
          <TableCell>
            <div className="space-y-2">
              {Object.entries(category.translations).map(([lang, names]) => (
                <div key={lang} className="flex flex-wrap gap-2">
                  {(Array.isArray(names) ? names : [names]).map((name, idx) => (
                    <span
                      key={`${lang}-${idx}`}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-primary/10 text-primary"
                    >
                      <span className="font-bold mr-1">{lang}:</span>
                      {name}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </TableCell>
          <TableCell className="text-right">
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog(undefined, true, category.id)}
                title="Alkategória hozzáadása"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog(category)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(category.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>

        {isExpanded && subcategories.length > 0 && (
          <>
            {subcategories.map(subcategory => renderCategoryRow(subcategory, level + 1))}
          </>
        )}

        {isExpanded && level === 0 && (
          <TableRow>
            <TableCell colSpan={3} className="py-2" style={{ paddingLeft: `${(level + 1) * 2}rem` }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openDialog(undefined, true, category.id)}
                className="gap-2 w-full"
              >
                <Plus className="h-3 w-3" />
                Alkategória hozzáadása
              </Button>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Kategóriák</h1>
          <p className="mt-2 text-foreground/80">Kezeld az alkalmazás kategóriáit és alkategóriáit a különböző nyelveken.</p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Új főkategória
        </Button>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <p className="text-foreground/60">Még nincs hozzáadott kategória</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kategória</TableHead>
                <TableHead>Fordítások</TableHead>
                <TableHead className="text-right">Műveletek</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.filter(cat => cat.parent_id === null).map((category) => renderCategoryRow(category))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? `${isSubcategory ? 'Alkategória' : 'Kategória'} szerkesztése`
                : `Új ${isSubcategory ? 'alkategória' : 'főkategória'} hozzáadása`}
            </DialogTitle>
            <DialogDescription>
              Add meg a kategória neveit a különböző nyelveken.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Fordítások</Label>
              <div className="space-y-6">
                {selectedLanguages.map((langCode) => {
                  const langInfo = languages.find(l => l.code === langCode);
                  const names = formData.translations[langCode] || [''];
                  return (
                    <div key={langCode} className="border rounded-lg p-4 space-y-2 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">
                          {langInfo?.description} ({langCode})
                          {langCode === 'HU' && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {langCode !== 'HU' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLanguage(langCode)}
                            className="text-destructive hover:text-destructive"
                          >
                            ✕ Egész nyelv eltávolítása
                          </Button>
                        )}
                      </div>

                      <div className="space-y-2">
                        {names.map((name, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              placeholder={`${langInfo?.description} név ${idx + 1}.`}
                              value={name}
                              onChange={(e) =>
                                handleTranslationChange(langCode, idx, e.target.value)
                              }
                              required={langCode === 'HU' && names.length === 1}
                            />
                            {names.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveAlias(langCode, idx)}
                                className="text-destructive"
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddAlias(langCode)}
                        className="w-full gap-2"
                      >
                        <Plus className="h-3 w-3" />
                        +{langCode} alias hozzáadása
                      </Button>
                    </div>
                  );
                })}
              </div>

              {getAvailableLanguagesToAdd().length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium mb-3 block">Új nyelv hozzáadása:</Label>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableLanguagesToAdd().map((lang) => (
                      <Button
                        key={lang.code}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddLanguage(lang.code)}
                        className="gap-2"
                      >
                        <Plus className="h-3 w-3" />
                        +{lang.code}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDialog}>
                Mégse
              </Button>
              <Button onClick={handleSave}>
                {isEditing ? 'Frissítés' : 'Hozzáadás'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesPage;